import { useQuery } from '@tanstack/react-query';
import { KYCQueryKeys } from '../../queryKeys';
import { Nullable } from '@starsoft/common/interfaces';
import { AxiosError, AxiosInstance } from 'axios';
import { fetchKycMeByOriginId } from './service';
import { UseKycMeProps, UseKycMeQueryKeyProps } from './props';
import {
  CommonModalsKey,
  CookiesKey,
  KycIdentityOriginId,
  KYCStatus,
} from '@starsoft/common/enums';
import { GenericError, Kyc } from '@starsoft/common/models';
import {
  useAxiosInstance,
  useCurrentModal,
  useNavigate,
} from '@starsoft/common/hooks';
import { KycCheckpointStatus } from '@starsoft/common/enums/kyc/checkpoint/status';
import { useEffect, useMemo } from 'react';
import { getCookie, setCookie } from 'cookies-next';
import { AuthService } from '@starsoft/common/services';

/**
 * Custom hook to fetch and manage KYC (Know Your Customer) data for a user by origin ID
 *
 * This hook handles fetching KYC data for the authenticated user based on their origin ID.
 * It uses React Query for data fetching, caching, and state management.
 * The hook also tracks the first incomplete KYC verification step.
 *
 * @param {UseKycMeProps} options - Optional configuration object
 * @param {boolean} [options.enabled=true] - Whether the query should automatically run
 * @param {string} [options.originId=KycIdentityOriginId.Brazil] - The origin ID to fetch KYC data for
 *
 * @returns {Object} An object containing:
 *   - kyc: The fetched KYC data or null
 *   - isPending: Boolean indicating if initial fetch is in progress
 *   - error: Any error that occurred during fetch
 *   - refetch: Function to manually trigger a refetch
 *   - isSuccess: Boolean indicating if fetch was successful
 *   - promise: Promise of the current query
 *   - isFetching: Boolean indicating if any fetch is in progress
 *   - firstValidStep: ID of first incomplete KYC checkpoint
 *   - fetchKycMeByOriginId: The fetch function used internally
 */
export function useKycMeByOriginId(options?: UseKycMeProps) {
  const { enabled = true, originId = KycIdentityOriginId.Brazil?.toString() } =
    options ?? {};
  const queryKey: UseKycMeQueryKeyProps = [KYCQueryKeys.Me, originId];
  const axiosInstance: AxiosInstance = useAxiosInstance();
  const navigate = useNavigate();
  const modal = useCurrentModal(CommonModalsKey);

  // Check if user has valid authentication tokens
  const isPartialAuthorized =
    !!getCookie(CookiesKey.Token) && !!getCookie(CookiesKey.RefreshToken);

  // Set up the main query to fetch KYC data
  const {
    data,
    isPending,
    fetchStatus,
    error,
    refetch,
    isSuccess,
    isFetching,
    promise,
  } = useQuery<
    Nullable<Kyc>,
    AxiosError<GenericError>,
    Nullable<Kyc>,
    UseKycMeQueryKeyProps
  >({
    queryKey,
    queryFn: queryContext => fetchKycMeByOriginId(queryContext, axiosInstance),
    staleTime: 5 * 1000, // Consider data stale after 5 seconds
    enabled: !!originId && enabled && isPartialAuthorized,
  });

  // Calculate the first incomplete KYC checkpoint
  const firstValidStep: Nullable<string> = useMemo(() => {
    if (!data) {
      return null;
    }

    return (
      data?.tracking
        ?.find(track => track?.status !== KycCheckpointStatus.Verified)
        ?.checkpoint?.id?.toString() ?? data?.stage?.checkpoint?.id?.toString()
    );
  }, [data]);

  function handleSetAuthorized() {
    if (!data || data?.status !== KYCStatus.Verified) {
      return;
    }

    if (modal && modal === CommonModalsKey.Login) {
      setCookie(CookiesKey.HasKyc, 'true');
      AuthService.setAsAuthorized();
      navigate({
        pathname: '/',
      });
    }
  }

  useEffect(handleSetAuthorized, [data, navigate, modal]);

  return {
    kyc: data,
    isPending: isPending && fetchStatus !== 'idle',
    error,
    refetch,
    isSuccess,
    promise,
    isFetching,
    firstValidStep,
    fetchKycMeByOriginId,
  };
}
